import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '../../../common/Card'
import { CardBody } from '../../../common/Card/CardBody'
import { Icon, IconChoices } from '../../../common/Icon'
import { theme } from '../../../../theme/theme'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { getSocietyType } from '../../../../helpers/translations/societyType'
import { SocietyTypes } from '../../../../types/society-type'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { useAuth } from '../../../../hooks/useAuth'
import { societyQueries } from '../../../../api/societies'
import { requestQueries } from '../../../../api/requests'

interface CardContentProps {
  society: ISocietiesModel
}

const CardContent = ({ society }: CardContentProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { userId } = useAuth()

  const { data: userSocieties } = useQuery(
    societyQueries.getUserSocieties(userId)
  )
  const { data: userRequests } = useQuery(
    requestQueries.getRequestsPerUser(userId)
  )

  const userSocietyIds = userSocieties?.map((_society) => _society._id)

  const societyIsActive = society.status === 'active'

  const userMemberOfSociety = userSocietyIds?.includes(society._id)
  const userHasPendingRequest = userRequests?.find(
    (request) => request.societyId === society._id
  )

  const getIconText = (): string => {
    if (userHasPendingRequest) {
      return translate('findSociety.labels.hasPendingRequest')
    }
    if (userMemberOfSociety) {
      return translate('findSociety.labels.userMemberOfSociety')
    }
    if (societyIsActive) {
      return translate('common.active')
    }

    return ''
  }

  const iconText = getIconText()

  const societyType = society?.societyTypeDisplayName
    ? translate(
        getSocietyType(society.societyTypeDisplayName as SocietyTypes) as string
      )
    : undefined

  const showIcon =
    societyIsActive || userMemberOfSociety || userHasPendingRequest

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex flex-col items-start">
        <p style={theme.textVariants.baseBold} translate="no">
          {society?.name}
        </p>
        <p style={theme.textVariants.caption}>{`${societyType} ${translate(
          'common.in'
        )} ${society?.addressCity}`}</p>
      </div>
      {showIcon && (
        <div className="flex w-10 flex-col items-center">
          <Icon
            icon={IconChoices.CHECKMARK_ROUND}
            color={theme.colors.semanticGreen}
          />
          <p style={theme.textVariants.captionBold}>{iconText}</p>
        </div>
      )}
    </div>
  )
}

interface FindSocietyListItemProps {
  society: ISocietiesModel
  selected: boolean
  onClick?: () => void
}

export const FindSocietyListItem = ({
  society,
  selected,
  onClick,
}: FindSocietyListItemProps): React.ReactNode => {
  return (
    <Card
      className={`border-none shadow-none ${selected ? 'bg-neutral-96' : ''}`}
      onClick={onClick}
    >
      {{
        body: (
          <CardBody>
            <CardContent society={society} />
          </CardBody>
        ),
      }}
    </Card>
  )
}
